import { FileObjectWithTypeOverride } from "components/documents/RecentDocument";
import qs from "qs";
import { fileAndFolder, parents_list } from "types/Models/documents";
import { emptyApi } from "./emptyApi";
import { FileUrl } from "types/Models/fileObject";
import { DocumentTag } from "types/Models/documentTagReview";

type Result = {
  files: fileAndFolder[];
  parents_list: parents_list[];
  subfolders: fileAndFolder[];
  folders: fileAndFolder[];
  current_page: number;
  current_folder?: {
    uuid: string;
    name: string;
    is_supporting_documents_folder: boolean;
  };
};

export const documentApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getTagDocuments: build.query<
      Result,
      {
        tag_title?: string;
        groupId: string;
      }
    >({
      query: ({ tag_title, groupId }) => {
        let query = qs.stringify(
          {
            tag_title,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/documents/group/${groupId}/tag-documents/${query}`,
        };
      },
    }),

    getFolderContent: build.query<
      Result,
      {
        folderId: string;
        groupId: string;
        tags?: string[];
      }
    >({
      query: ({ folderId, groupId, tags }) => {
        const params =
          tags && tags.length > 0
            ? `?${tags.map((t) => `tags=${t}`).join("&")}`
            : "";
        return {
          url: `/api/inkle/documents/group/${groupId}/folder/${folderId}/list/${params}`,
        };
      },
      providesTags: ["DOCUMENT_LIST"],
    }),

    getEntityFolder: build.query<
      { uuid: string },
      { entityId: string; folderPath: string[] }
    >({
      query: ({ entityId, folderPath }) => ({
        url: `/api/inkle/documents/entity/${entityId}/folder/`,
        params: { folder_path: folderPath.join(",") },
      }),
    }),

    getSupportingDocumentTags: build.query<DocumentTag[], { groupId: string }>({
      query: ({ groupId }) => ({
        url: `/api/inkle/documents/group/${groupId}/supporting-document-tags/`,
      }),
    }),

    getSearchContent: build.query<
      Result,
      {
        groupId: string;
        searchTerm: string | null;
        pageNum: number;
      }
    >({
      query: ({ groupId, searchTerm, pageNum }) => {
        let query = qs.stringify(
          {
            search_term: searchTerm,
            page_num: pageNum,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/documents/group/${groupId}/search/${query}`,
        };
      },
    }),
    exportFolder: build.mutation<
      FileUrl,
      { groupId: string; fileId?: string; export_to_mail?: boolean }
    >({
      query: ({ groupId, fileId, export_to_mail }) => {
        return {
          url: `/api/inkle/documents/group/${groupId}/export/`,
          method: "POST",
          body: { file_ids: fileId, export_to_mail },
        };
      },
    }),
    recentDocuments: build.query<
      FileObjectWithTypeOverride,
      { groupId: string }
    >({
      query: ({ groupId }) => {
        return {
          url: `/api/inkle/documents/group/${groupId}/recent-documents/`,
        };
      },
      providesTags: ["RECENT_DOCUMENT"],
    }),
    renameFileFolder: build.mutation<
      any,
      {
        groupId: string;
        fileId?: string;
        payload: { file_name: string };
      }
    >({
      query: ({ groupId, fileId, payload }) => {
        return {
          url: `/api/inkle/documents/group/${groupId}/file/${fileId}/`,
          method: "PUT",
          body: payload,
        };
      },
      invalidatesTags: ["RECENT_DOCUMENT"],
    }),
    deleteFileFolder: build.mutation<
      any,
      {
        groupId: string;
        folderId?: string;
      }
    >({
      query: ({ groupId, folderId }) => {
        return {
          url: `/api/inkle/documents/group/${groupId}/file/${folderId}/`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["RECENT_DOCUMENT"],
    }),

    uploadFile: build.mutation<
      fileAndFolder[],
      {
        groupId: string;
        parentFolderId: string;
        files: File[];
      }
    >({
      query: ({ groupId, parentFolderId, files }) => {
        const formData = new FormData();
        formData.append("parent_uuid", parentFolderId);
        for (let i = 0; i < files.length; i++) {
          formData.append("files", files[i]);
        }

        return {
          url: `/api/inkle/documents/group/${groupId}/files/upload/`,
          method: "POST",
          body: formData,
        };
      },
    }),

    createFolder: build.mutation<
      fileAndFolder,
      {
        groupId: string;
        parentFolderId: string;
        folder_name: string;
        is_private?: boolean;
      }
    >({
      query: ({ groupId, parentFolderId, folder_name, is_private }) => {
        return {
          url: `/api/inkle/documents/group/${groupId}/folder/create/`,
          method: "POST",
          body: { folder_name, parent_uuid: parentFolderId, is_private },
        };
      },
      invalidatesTags: ["DOCUMENT_LIST"],
    }),

    moveDocItems: build.mutation<
      fileAndFolder,
      {
        groupId: string;
        file_uuids: string[];
        destination_folder_id: string;
      }
    >({
      query: ({ groupId, ...rest }) => {
        return {
          url: `/api/inkle/documents/group/${groupId}/files/move/`,
          method: "POST",
          body: rest,
        };
      },
    }),

    getParsedData: build.query<
      {
        response: string;
      },
      {
        files: File[];
        prompt: string;
      }
    >({
      query: ({ files, prompt }) => {
        const formData = new FormData();
        for (let i = 0; i < files.length; i++) {
          formData.append("files", files[i]);
        }
        formData.append("prompt", prompt);
        return {
          url: `/api/inkle/documents/parse/`,
          method: "POST",
          body: formData,
        };
      },
    }),

    getRecycleBinContent: build.query<
      fileAndFolder[],
      {
        groupId: string;
        searchTerm?: string;
      }
    >({
      query: ({ groupId, searchTerm }) => {
        const query = qs.stringify(
          { search_term: searchTerm },
          { skipNulls: true, addQueryPrefix: true }
        );
        return `/api/inkle/documents/group/${groupId}/recycle-bin/${query}`;
      },
      providesTags: ["RECYCLE_BIN_DOCUMENT_LIST"],
    }),

    addToRecycleBin: build.mutation<
      Result,
      {
        groupId: string;
        files: string[];
      }
    >({
      query: ({ groupId, files }) => {
        return {
          url: `/api/inkle/documents/group/${groupId}/recycle-bin/`,
          method: "POST",
          body: { files },
        };
      },
      invalidatesTags: [
        "RECYCLE_BIN_DOCUMENT_LIST",
        "DOCUMENT_LIST",
        "RECENT_DOCUMENT",
      ],
    }),

    deleteFromRecycleBin: build.mutation<
      Result,
      {
        groupId: string;
        files: string[];
      }
    >({
      query: ({ groupId, files }) => {
        return {
          url: `/api/inkle/documents/group/${groupId}/recycle-bin/`,
          method: "DELETE",
          body: { files },
        };
      },
      invalidatesTags: ["RECYCLE_BIN_DOCUMENT_LIST"],
    }),

    restoreFromRecycleBin: build.mutation<
      Result,
      {
        groupId: string;
        files: string[];
      }
    >({
      query: ({ groupId, files }) => {
        return {
          url: `/api/inkle/documents/group/${groupId}/restore-from-recycle-bin/`,
          method: "POST",
          body: { files },
        };
      },
      invalidatesTags: [
        "RECYCLE_BIN_DOCUMENT_LIST",
        "DOCUMENT_LIST",
        "RECENT_DOCUMENT",
      ],
    }),
  }),

  overrideExisting: false,
});

export const {
  useGetTagDocumentsQuery,
  useGetFolderContentQuery,
  useLazyGetFolderContentQuery,
  useGetEntityFolderQuery,
  useGetSupportingDocumentTagsQuery,
  useGetSearchContentQuery,
  useLazyGetSearchContentQuery,
  useExportFolderMutation,
  useRecentDocumentsQuery,
  useRenameFileFolderMutation,
  useDeleteFileFolderMutation,
  useUploadFileMutation,
  useCreateFolderMutation,
  useMoveDocItemsMutation,
  useGetParsedDataQuery,
  useGetRecycleBinContentQuery,
  useAddToRecycleBinMutation,
  useDeleteFromRecycleBinMutation,
  useRestoreFromRecycleBinMutation,
} = documentApi;
